import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import SignaturePad from 'signature_pad';

interface SignaturePadProps {
    penColor?: string;
    backgroundColor?: string;
    canvasProps?: {
        width?: number;
        height?: number;
        backgroundColor?: string;
    };
    onEnd?: () => void;
}

export interface SignaturePadRef {
    isEmpty: () => boolean;
    clear: () => void;
    toDataURL: (type?: string, encoderOptions?: number) => string;
}

const SignaturePadComponent = forwardRef<SignaturePadRef, SignaturePadProps>(
    ({ penColor = 'black', backgroundColor = 'rgb(255, 255, 255)', canvasProps, onEnd }, ref) => {
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const signaturePadRef = useRef<SignaturePad | null>(null);
        const onEndRef = useRef(onEnd);
        onEndRef.current = onEnd;

        useEffect(() => {
            if (!canvasRef.current) return;
            const canvas = canvasRef.current;
            const width = canvasProps?.width ?? 330;
            const height = canvasProps?.height ?? 100;
            const bgColor = canvasProps?.backgroundColor ?? backgroundColor;
            canvas.width = width;
            canvas.height = height;
            canvas.style.backgroundColor = bgColor;

            signaturePadRef.current = new SignaturePad(canvas, {
                backgroundColor: bgColor,
                penColor: penColor,
            });

            const handleEnd = () => {
                if (onEndRef.current) onEndRef.current();
            };
            signaturePadRef.current.addEventListener('endStroke', handleEnd);

            return () => {
                if (signaturePadRef.current) {
                    signaturePadRef.current.removeEventListener('endStroke', handleEnd);
                    signaturePadRef.current.clear();
                }
            };
        }, []);

        useImperativeHandle(ref, () => ({
            isEmpty: () => {
                return signaturePadRef.current ? signaturePadRef.current.isEmpty() : true;
            },
            clear: () => {
                if (signaturePadRef.current) {
                    signaturePadRef.current.clear();
                }
            },
            toDataURL: (type?: string, encoderOptions?: number) => {
                return signaturePadRef.current ? signaturePadRef.current.toDataURL(type, encoderOptions) : '';
            },
        }));

        return <canvas ref={canvasRef} />;
    }
);

SignaturePadComponent.displayName = 'SignaturePad';

export default SignaturePadComponent;
